import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import {
  ChevronLeft, ChevronRight, GraduationCap, BookOpen,
  Code, Server, Radio, Zap, Cog, Building,
  Cpu, Database, Gauge, Factory, FlaskConical, Beaker
} from "lucide-react";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

const VTU_LOGO = "https://customer-assets.emergentagent.com/job_prism-labs-2/artifacts/s5v94ogg_image.png";
const BMSCE_LOGO = "https://customer-assets.emergentagent.com/job_prism-labs-2/artifacts/czx8x0ru_image.png";

const UNIVERSITIES = {
  vtu: { name: "Visvesvaraya Technological University", short_name: "VTU", location: "Belagavi, Karnataka", image: VTU_LOGO },
  bmsce: { name: "BMS College of Engineering (Autonomous)", short_name: "BMSCE", location: "Bangalore, Karnataka", image: BMSCE_LOGO },
};

// Icon mapping for branches
const branchIcons = {
  code: Code,
  server: Server,
  radio: Radio,
  zap: Zap,
  cog: Cog,
  building: Building,
  cpu: Cpu,
  database: Database,
  gauge: Gauge,
  factory: Factory,
  flask: FlaskConical,
  beaker: Beaker,
};

// Color gradients for branches
const branchColors = [
  "from-violet-500 to-purple-600",
  "from-cyan-500 to-blue-600",
  "from-pink-500 to-rose-600",
  "from-emerald-500 to-green-600",
  "from-amber-500 to-orange-600",
  "from-indigo-500 to-violet-600",
  "from-teal-500 to-cyan-600",
  "from-red-500 to-pink-600",
  "from-lime-500 to-green-600",
  "from-fuchsia-500 to-purple-600",
  "from-sky-500 to-blue-600",
  "from-orange-500 to-red-600",
];

export default function UniversityPage() {
  const { uniId } = useParams();
  const [branches, setBranches] = useState([]);
  const university = UNIVERSITIES[uniId];

  useEffect(() => {
    axios.get(`${API}/branches`).then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  const getIcon = (iconName) => {
    return branchIcons[iconName] || Code;
  };

  if (!university) {
    return (
      <div className="min-h-screen pt-24 text-center">
        <div className="text-zinc-500 mb-4">University not found</div>
        <Link to="/" className="text-violet-400 hover:text-violet-300">Go back home</Link>
      </div>
    );
  }

  return (
    <div data-testid="university-page" className="min-h-screen pt-24 pb-12">
      <div className="absolute top-0 left-0 w-full h-[400px] bg-gradient-to-b from-violet-600/5 to-transparent pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          {/* Back button */}
          <motion.div variants={fadeInUp} className="mb-6">
            <Link to="/" className="flex items-center gap-2 text-sm text-zinc-500 hover:text-white transition-colors">
              <ChevronLeft className="w-4 h-4" />Back to Universities
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div variants={fadeInUp} className="flex items-center gap-5 mb-10">
            {university.image ? (
              <img src={university.image} alt={university.short_name}
                className="w-20 h-20 rounded-xl object-contain bg-white/5 border border-white/10 p-2 shrink-0" />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-violet-600/10 border border-white/10 flex items-center justify-center shrink-0">
                <GraduationCap className="w-10 h-10 text-violet-400" />
              </div>
            )}
            <div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white">{university.short_name}</h1>
              <p className="text-sm text-zinc-500 mt-1">{university.name}</p>
              <p className="text-xs text-zinc-600 mt-0.5">{university.location}</p>
            </div>
          </motion.div>

          {/* Branches */}
          <motion.div variants={fadeInUp} className="mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-2">Select Your Branch</h2>
            <p className="text-sm text-zinc-500">Choose your department to browse study materials</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {branches.map((branch, idx) => {
              const IconComponent = getIcon(branch.icon);
              const colorClass = branchColors[idx % branchColors.length];
              return (
                <motion.div key={branch.code} variants={fadeInUp}>
                  <Link
                    to={`/university/${uniId}/branch/${branch.code}`}
                    data-testid={`branch-card-${branch.code}`}
                    className="group block rounded-2xl bg-white/[0.03] border border-white/10 hover:border-violet-500/30 hover:-translate-y-1 hover:shadow-[0_0_40px_-10px_rgba(139,92,246,0.15)] overflow-hidden p-5"
                    style={{ transitionProperty: "border-color, transform, box-shadow", transitionDuration: "300ms" }}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`shrink-0 rounded-xl bg-gradient-to-br ${colorClass} p-3 group-hover:scale-110`}
                        style={{ transitionProperty: "transform", transitionDuration: "300ms" }}>
                        <IconComponent className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-base font-bold text-white mb-0.5 group-hover:text-violet-200 transition-colors">
                          {branch.code}
                        </h3>
                        <p className="text-xs text-zinc-500 line-clamp-2">{branch.name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-violet-400 group-hover:text-violet-300 transition-colors mt-3">
                      Browse Resources <ChevronRight className="w-3 h-3" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
